import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Typography, Button, Box, IconButton, Avatar, Menu, MenuItem, useMediaQuery, useTheme as useMuiTheme } from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase.ts';
import './Header.css';
import { useTheme } from '../ThemeContext.tsx';
import { Badge } from '@mui/material';
import { NavLink } from 'react-router-dom';
import { User } from 'firebase/auth';

interface HeaderProps {
  user: User | null;
}

function Header({ user }: HeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm')); // This hook is still needed for the component to compile, but its value won't be used for rendering logic within this component.
  const { theme, customLogoUrl, customHeaderText, isSchedulePageVisible } = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const open = Boolean(anchorEl);

  useEffect(() => {
    let unsubscribe: () => void;
    if (user) {
      const notificationsRef = collection(db, `users/${user.uid}/notifications`);
      const q = query(notificationsRef, where('read', '==', false));

      unsubscribe = onSnapshot(q, (snapshot) => {
        setUnreadCount(snapshot.size);
      }, (error: any) => {
        console.error("Error fetching unread notifications:", error);
      });
    } else {
      setUnreadCount(0);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const renderLogo = (): React.ReactElement => {
    if (customLogoUrl) {
      return <img src={customLogoUrl} alt="App Logo" className="app-logo" onClick={() => navigate('/')} />;
    }
    if (customHeaderText) {
      return (
        <Typography variant="h6" className="logo" onClick={() => navigate('/')}>
          {customHeaderText}
        </Typography>
      );
    }
    return (
      <Typography variant="h6" className="logo" onClick={() => navigate('/')}>
        CheckifAI
      </Typography>
    );
  };

  return (
    <AppBar position="static" sx={{ backgroundColor: theme.colors.whiteCardBackground, boxShadow: 'none' }}>
      <Toolbar sx={{ justifyContent: 'space-between', padding: '0 20px' }}>
        {renderLogo()}
        <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'center' }}>
          <Button
            onClick={() => handleMenuItemClick('/')}
            sx={{
              color: location.pathname === '/' ? theme.colors.primaryRed : theme.colors.darkText,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                color: theme.colors.primaryRed,
              },
            }}
          >
            Dashboard
          </Button>
          <Button
            onClick={() => handleMenuItemClick('/checklists')}
            sx={{
              color: location.pathname === '/checklists' ? theme.colors.primaryRed : theme.colors.darkText,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                color: theme.colors.primaryRed,
              },
            }}
          >
            Checklists
          </Button>
          {isSchedulePageVisible && (
            <Button
              onClick={() => handleMenuItemClick('/scheduled-tasks')}
              sx={{
                color: location.pathname === '/scheduled-tasks' ? theme.colors.primaryRed : theme.colors.darkText,
                fontWeight: 'bold',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: theme.colors.primaryRed,
                },
              }}
            >
              Scheduled Tasks
            </Button>
          )}
          <Button
            onClick={() => handleMenuItemClick('/reports')}
            sx={{
              color: location.pathname === '/reports' ? theme.colors.primaryRed : theme.colors.darkText,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                color: theme.colors.primaryRed,
              },
            }}
          >
            Reports
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {user && (
            <>
              <IconButton onClick={() => navigate('/notifications')} sx={{ color: theme.colors.darkText }}>
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <Avatar
                alt={user.displayName ?? ''}
                src={user.photoURL ?? ''}
                sx={{ cursor: 'pointer', color: theme.colors.darkText, backgroundColor: theme.colors.lightGrayBackground }}
                onClick={() => navigate('/settings')}
              />
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default Header;
